import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: ToastAction;
  dismissible?: boolean;
  showProgress?: boolean;
  icon?: React.ReactNode;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  action,
  dismissible = true,
  showProgress = true,
  icon: customIcon,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Slide in animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (duration <= 0) return;

    const startTimer = () => {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      setTimeoutId(timer);
    };

    const clearTimer = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
    };

    if (isHovered) {
      clearTimer();
    } else {
      startTimer();
    }

    return clearTimer;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered, duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300); // Wait for exit animation
  };

  const getIcon = () => {
    if (customIcon) return customIcon;
    
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500 dark:text-yellow-400 flex-shrink-0" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-500 dark:text-blue-400 flex-shrink-0" />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700';
      case 'info':
      default:
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700';
    }
  };

  const getProgressColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500 dark:bg-green-400';
      case 'error':
        return 'bg-red-500 dark:bg-red-400';
      case 'warning':
        return 'bg-yellow-500 dark:bg-yellow-400';
      case 'info':
      default:
        return 'bg-blue-500 dark:bg-blue-400';
    }
  };

  // ARIA live region configuration based on toast type
  const getAriaAttributes = (): {
    role: 'alert' | 'status';
    'aria-live': 'assertive' | 'polite';
    'aria-atomic': 'true';
  } => {
    // Errors are assertive (interrupt), others are polite (wait)
    const ariaLive: 'assertive' | 'polite' = type === 'error' ? 'assertive' : 'polite';
    // Alert role for errors/warnings, status for success/info
    const role: 'alert' | 'status' = (type === 'error' || type === 'warning') ? 'alert' : 'status';
    
    return {
      role,
      'aria-live': ariaLive,
      'aria-atomic': 'true' as const
    };
  };

  return (
    <div
      {...getAriaAttributes()}
      className={`
        relative min-w-96 max-w-lg w-auto bg-white dark:bg-slate-800 border rounded-lg shadow-lg pointer-events-auto
        transform transition-all duration-300 ease-in-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${getBackgroundColor()}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
              {title}
            </p>
            {message && (
              <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
                {message}
              </p>
            )}
            {/* Action Button */}
            {action && (
              <button
                onClick={() => {
                  action.onClick();
                  handleClose();
                }}
                className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors underline"
              >
                {action.label}
              </button>
            )}
          </div>
          {dismissible && (
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className="bg-white dark:bg-slate-800 rounded-md inline-flex text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-colors"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Progress bar */}
      {showProgress && duration > 0 && !isHovered && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-slate-700 rounded-b-lg overflow-hidden">
          <div
            className={`h-full ${getProgressColor()}`}
            style={{
              animation: `toast-shrink ${duration}ms linear`,
              transformOrigin: 'left'
            }}
          />
        </div>
      )}
      
      <style>
        {`
          @keyframes toast-shrink {
            from {
              transform: scaleX(1);
            }
            to {
              transform: scaleX(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default Toast;
